import { normalizeEmail } from "@/lib/userSession";

// Per-client dashboard gates driven by the client-ops record:
// - eligibility_assessment: one-time in-dashboard assessment state
// - dashboard_access: admin-granted unlock for the rest of the dashboard
// - selected_grants: grants the client chose from their assessment matches
const CLIENT_GATES_KEY = "optra_client_gates_v1";
const CLIENT_GATES_EVENT = "optra-client-gates-updated";

const DEFAULT_GATES = {
  eligibilityAssessment: {
    status: "",
    jobId: "",
    startedAt: "",
    completedAt: "",
    source: "",
  },
  dashboardAccess: {
    isGranted: false,
    grantedAt: "",
    grantedBy: "",
  },
  selectedGrants: [],
};

const readAll = () => {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(CLIENT_GATES_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const writeAll = (rows) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(CLIENT_GATES_KEY, JSON.stringify(rows || {}));
  } catch {
    // ignore storage write failures
  }
};

const normalizeAssessmentState = (value = {}) => {
  const raw = value && typeof value === "object" ? value : {};
  let status = String(raw.status || "").trim().toLowerCase();
  if (["completed", "complete", "done", "succeeded"].includes(status)) status = "completed";
  else if (["in_progress", "in-progress", "running", "pending", "started"].includes(status)) status = "in_progress";
  else status = "";
  return {
    status,
    jobId: String(raw.jobId || raw.job_id || "").trim(),
    startedAt: String(raw.startedAt || raw.started_at || "").trim(),
    completedAt: String(raw.completedAt || raw.completed_at || "").trim(),
    source: String(raw.source || "").trim(),
  };
};

const normalizeDashboardAccess = (value = {}) => {
  const raw = value && typeof value === "object" ? value : {};
  const grantedAt = String(raw.granted_at || raw.grantedAt || "").trim();
  const status = String(raw.status || "").trim().toLowerCase();
  const isGranted = Boolean(
    raw.is_granted ||
    raw.isGranted ||
    grantedAt ||
    ["granted", "enabled", "active", "unlocked"].includes(status)
  );
  return {
    isGranted,
    grantedAt,
    grantedBy: String(raw.granted_by || raw.grantedBy || "").trim(),
  };
};

const normalizeSelectedGrants = (value) => {
  if (!Array.isArray(value)) return [];
  return value
    .filter((row) => row && typeof row === "object")
    .map((row) => ({
      code: String(row.code || row.name || "").trim(),
      name: String(row.name || row.code || "").trim(),
      score: Number.isFinite(Number(row.score)) ? Number(row.score) : null,
      selectedAt: String(row.selectedAt || row.selected_at || "").trim(),
    }))
    .filter((row) => row.code);
};

// Accepts either a full client-ops record (from record/me) or a stored gates row.
export const normalizeClientGates = (value = {}) => {
  const raw = value && typeof value === "object" ? value : {};
  const record = raw.record && typeof raw.record === "object" ? raw.record : raw;
  return {
    eligibilityAssessment: normalizeAssessmentState(
      record.eligibility_assessment || record.eligibilityAssessment
    ),
    dashboardAccess: normalizeDashboardAccess(
      record.dashboard_access || record.dashboardAccess
    ),
    selectedGrants: normalizeSelectedGrants(
      record.selected_grants || record.selectedGrants
    ),
  };
};

const dispatchChanged = (clientEmail, gates = null) => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(CLIENT_GATES_EVENT, {
    detail: { clientEmail: normalizeEmail(clientEmail || ""), gates },
  }));
};

export const getClientGates = (email) => {
  const key = normalizeEmail(email || "");
  if (!key) return { ...DEFAULT_GATES };
  const all = readAll();
  const row = all[key];
  if (row && typeof row === "object") return normalizeClientGates(row);
  return { ...DEFAULT_GATES };
};

export const isAssessmentComplete = (gates) =>
  normalizeClientGates(gates || {}).eligibilityAssessment.status === "completed";

export const isAssessmentInProgress = (gates) =>
  normalizeClientGates(gates || {}).eligibilityAssessment.status === "in_progress";

export const hasDashboardAccess = (gates) =>
  normalizeClientGates(gates || {}).dashboardAccess.isGranted;

export const upsertClientGates = (email, recordOrGates = {}) => {
  const key = normalizeEmail(email || "");
  if (!key) return null;
  const next = normalizeClientGates(recordOrGates);
  const all = readAll();
  // Unchanged gates are neither rewritten nor announced. Every dashboard surface that reads the
  // backend record writes it back here, and the sidebar refetches on the announcement, so a
  // no-op write would otherwise refetch, rewrite and announce again without end.
  const current = all[key] && typeof all[key] === "object" ? normalizeClientGates(all[key]) : null;
  if (current && JSON.stringify(current) === JSON.stringify(next)) return next;
  all[key] = next;
  writeAll(all);
  dispatchChanged(key, next);
  return next;
};

/**
 * Drop a stale `in_progress` assessment gate (e.g. the job failed or timed out)
 * so /dashboard/eligibility-assessment shows the checker again instead of
 * redirecting back to the progress page. Completed gates are left alone.
 */
export const clearAssessmentInProgress = (email) => {
  const key = normalizeEmail(email || "");
  if (!key) return null;
  const current = getClientGates(key);
  if (current.eligibilityAssessment.status !== "in_progress") return current;
  return upsertClientGates(key, {
    ...current,
    eligibilityAssessment: { ...DEFAULT_GATES.eligibilityAssessment },
  });
};

export const subscribeToClientGates = (callback) => {
  if (typeof window === "undefined" || typeof callback !== "function") return () => {};
  const handler = () => callback();
  window.addEventListener(CLIENT_GATES_EVENT, handler);
  window.addEventListener("storage", handler);
  return () => {
    window.removeEventListener(CLIENT_GATES_EVENT, handler);
    window.removeEventListener("storage", handler);
  };
};
